// src/components/portal/PortalLogin.js
// Shared login page for funders and supervisors.
// Flow: enter email → sign in anonymously → check Firestore → redirect to dashboard
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../shared/legacyData/legacyCallable";
import "./Portal.css";

// TPC logo — update this path to match your project
import TPC_LOGO from "../../images/TPC-logo-light.png";

const COMPANY          = "TPC_Group";
const COLLECTION_BRAND = "TechPerformanceCentre";

export default function PortalLogin() {
const navigate  = useNavigate();
const location  = useLocation();

// Determine role from the URL path: /funder-login or /supervisor-login
const role = location.pathname.includes("supervisor") ? "supervisor" : "funder";
const roleLabel = role === "funder" ? "Funder" : "Supervisor";

const [email,   setEmail]   = useState("");
const [loading, setLoading] = useState(false);
const [error,   setError]   = useState("");

// If already logged in as portal user, skip login
useEffect(() => {
const stored = sessionStorage.getItem("portalUser");
if (stored) {
    try {
    const user = JSON.parse(stored);
    navigate(user.role === "funder" ? "/funder-dashboard" : "/supervisor-dashboard", { replace: true });
    } catch { sessionStorage.removeItem("portalUser"); }
}
}, [navigate]);

async function handleLogin(e) {
e.preventDefault();
const trimmed = email.trim().toLowerCase();
if (!trimmed) { setError("Please enter your email address."); return; }
if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError("Please enter a valid email address."); return; }

setLoading(true);
setError("");

try {
    // Step 1: Sign in anonymously so we can call auth-protected Cloud Functions
    const auth = getAuth();
    if (!auth.currentUser) {
    await signInAnonymously(auth);
    }

    const getSystemDocuments = httpsCallable(getFunctions(), "getSystemDocuments");

    if (role === "funder") {
    // ── Check funders collection ────────────────────────────────────────
    const res  = await getSystemDocuments({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "funders", auth: true });
    const data = Array.isArray(res?.data?.data) ? res.data.data : Object.values(res?.data?.data || {});
    const funder = data.find((f) =>
        (f.ContactOfficerEmail || f.contactOfficerEmail || "").toLowerCase() === trimmed,
    );

    if (funder) {
        sessionStorage.setItem("portalUser", JSON.stringify({
        role:  "funder",
        email: trimmed,
        id:    funder.id,
        name:  funder.ContactOfficerName || funder.contactOfficerName || trimmed,
        funder,
        }));
        navigate("/funder-dashboard", { replace: true });
        return;
    }

    setError("No funder account found for this email address. Please check your email or contact TPC.");

    } else {
    // ── Check supervisors collection ────────────────────────────────────
    const res  = await getSystemDocuments({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "supervisors", auth: true });
    const data = Array.isArray(res?.data?.data) ? res.data.data : Object.values(res?.data?.data || {});
    const supervisor = data.find((s) =>
        (s.email || "").toLowerCase() === trimmed,
    );

    if (supervisor) {
        sessionStorage.setItem("portalUser", JSON.stringify({
        role:       "supervisor",
        email:      trimmed,
        id:         supervisor.id,
        name:       `${supervisor.name || ""} ${supervisor.surname || ""}`.trim(),
        supervisor,
        }));
        navigate("/supervisor-dashboard", { replace: true });
        return;
    }

    setError("No supervisor account found for this email address. Please check your email or contact TPC.");
    }
} catch (err) {
    console.error("[PortalLogin] error:", err);
    setError("Unable to verify your account. Please try again or contact TPC support.");
} finally {
    setLoading(false);
}
}

return (
<div className="portal-login-page">
    <div className="portal-login-card">
    <img src={TPC_LOGO} alt="TPC" className="portal-login__logo" onError={(e) => { e.target.style.display = "none"; }} />

    <div style={{ textAlign: "center" }}>
        <span className="portal-login__role-badge">
        {role === "funder" ? "🏢" : "👤"} {roleLabel} Portal
        </span>
    </div>

    <h1 className="portal-login__title">Welcome back</h1>
    <p className="portal-login__sub">
        Enter the email address associated with your {roleLabel.toLowerCase()} account to access your dashboard.
    </p>

    <form onSubmit={handleLogin}>
        <label className="portal-login__label" htmlFor="portal-email">
        Email Address
        </label>
        <input
        id="portal-email"
        type="email"
        className={`portal-login__input${error ? " error" : ""}`}
        placeholder={`your.email@company.com`}
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(""); }}
        autoComplete="email"
        autoFocus
        />

        {error && <div className="portal-login__error">{error}</div>}

        <button type="submit" className="portal-login__btn" disabled={loading}>
        {loading && <span className="portal-login__spinner" />}
        {loading ? "Verifying…" : `Sign In as ${roleLabel}`}
        </button>
    </form>

    <button
        type="button"
        className="portal-login__back"
        onClick={() => navigate("/")}
        style={{ background: "none", border: 0, padding: 0, width: "100%" }}
    >
        ← Back to andus.co.za
    </button>
    </div>
</div>
);
}
